import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  TextInput,
  Button,
  Group,
  NumberInput,
  Text,
  Textarea
} from "@mantine/core";
import classes from "./edit.module.css";
import { RowDataMerchandise } from "./types";
import { Dropzone } from "@mantine/dropzone";
import { IconPhoto } from "@tabler/icons-react";

interface EditMerchandiseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEditMerchandise: (editedMerchandiseData: RowDataMerchandise) => void;
  merchandiseData: RowDataMerchandise;
}

const EditMerchandiseModal: React.FC<EditMerchandiseModalProps> = ({
  isOpen,
  onClose,
  onEditMerchandise,
  merchandiseData,
}) => {
  const [editedMerchandise, setEditedMerchandise] =
    useState<RowDataMerchandise>(merchandiseData);

  const imageDropzoneRef = useRef<() => void>(null);

  useEffect(() => {
    setEditedMerchandise(merchandiseData);
  }, [merchandiseData]);

  const handleImageUpload = (files: File[]) => {
    const file = files[0];
    if (file) {
      setEditedMerchandise({ ...editedMerchandise, imageSrc: file });
    }
  };

  const handleEdit = () => {
    // Perform validation or additional logic as needed
    onEditMerchandise(editedMerchandise);
    onClose();
  };

  return (
    <Modal
      title={
        <div style={{ textAlign: "center", fontWeight: "bold" }}>
          Edit Merchandise
        </div>
      }
      opened={isOpen}
      onClose={onClose}
    >

        <Text className={classes.Text}>
        Image <span className={classes.format}>(.jpg, .png)</span>
      </Text>
      <Dropzone
        onDrop={handleImageUpload}
        accept={["image/*"]}
        openRef={imageDropzoneRef}
      >
        <div className={classes.imageContainer}>
          {editedMerchandise.imageSrc ? (
            <img
              src={
                !editedMerchandise.imageSrc.path
                  ? editedMerchandise.imageSrc
                  : URL.createObjectURL(editedMerchandise.imageSrc)
              }
              alt="Image"
              className={classes.imageSize}
            />
          ) : (
            <p>
              <IconPhoto />
            </p>
          )}
        </div>
      </Dropzone>
      <Group>
        <Button
          onClick={() => imageDropzoneRef.current?.()}
          className={classes.imageButton}
        >
          <IconPhoto />
          Upload Image
        </Button>
      </Group>

      <TextInput
        label="Merchandise Name"
        mb="sm"
        maxLength={255}
        value={editedMerchandise.merchandiseName}
        onChange={(event) =>
          setEditedMerchandise({
            ...editedMerchandise,
            merchandiseName: event.currentTarget.value,
          })
        }
      />

      <TextInput
        label="Merchandise Model"
        mb="sm"
        maxLength={255}
        value={editedMerchandise.merchandiseModel}
        onChange={(event) =>
          setEditedMerchandise({
            ...editedMerchandise,
            merchandiseModel: event.currentTarget.value,
          })
        }
      />

    <Textarea
        label="Merchandise Description"
        placeholder="Merchandise Description"
        mb="sm"
        autosize={true}
        minRows={4}
        value={editedMerchandise.merchandiseDesc}
        onChange={(event) =>
            setEditedMerchandise({
            ...editedMerchandise,
            merchandiseDesc: event.currentTarget.value,
          })
        }
      />

      <NumberInput
        label="Quantity"
        mb="sm"
        allowDecimal={false}
        allowNegative={false}
        maxLength={255}
        value={editedMerchandise.quantity}
        onChange={(value) =>
          setEditedMerchandise({
            ...editedMerchandise,
            quantity: value.toString(),
          })
        }
      />

      <NumberInput
        label="Cost"
        mb="sm"
        maxLength={255}
        allowNegative={false}
        decimalScale={2}
        value={editedMerchandise.cost}
        onChange={(value) =>
          setEditedMerchandise({
            ...editedMerchandise,
            cost: value.toString(),
          })
        }
      />

      <Group className={classes.buttonBottom}>
        <Button onClick={() => onClose()} className={classes.buttonCancel}>
          Back
        </Button>
        <Button onClick={handleEdit} className={classes.buttonSaveChanges}>
          Save Changes
        </Button>
      </Group>
    </Modal>
  );
};

export default EditMerchandiseModal;
